import { api } from '@/convex/_generated/api';
import {
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery as useTanStackQuery,
} from '@tanstack/react-query';
import {
  type OptionalRestArgsOrSkip,
  useAction,
  useMutation,
  useQuery as useConvexQuery,
} from 'convex/react';
import type { FunctionReference } from 'convex/server';

// Re-export the API for convenience
export { api };

type QueryStatusResult<T> = {
  data: T | undefined;
  error: Error | null;
  isError: boolean;
  isPending: boolean;
  isSuccess: boolean;
  status: 'error' | 'pending' | 'success';
};

function isTanStackQueryOptions(
  value: unknown
): value is UseQueryOptions<unknown, Error, unknown, QueryKey> {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('queryKey' in value || 'queryFn' in value)
  );
}

export function useQuery<Query extends FunctionReference<'query'>>(
  query: Query,
  ...args: OptionalRestArgsOrSkip<Query>
): QueryStatusResult<Query['_returnType']>;

export function useQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError>;

export function useQuery(
  queryOrOptions: unknown,
  ...args: unknown[]
): QueryStatusResult<unknown> | UseQueryResult<unknown, Error> {
  const isOptions = isTanStackQueryOptions(queryOrOptions);
  const tanStackResult = useTanStackQuery(
    isOptions
      ? queryOrOptions
      : ({
          queryKey: ['__convex_disabled__'],
          queryFn: async () => undefined,
          enabled: false,
        } as UseQueryOptions<unknown, Error, unknown, QueryKey>)
  );
  const data = useConvexQuery(
    (isOptions ? api.users.queries.viewer : queryOrOptions) as never,
    (isOptions ? 'skip' : args[0]) as never
  );

  if (isOptions) {
    return tanStackResult;
  }

  return {
    data: data as unknown | undefined,
    error: null,
    isError: false,
    isPending: data === undefined,
    isSuccess: data !== undefined,
    status: data === undefined ? 'pending' : 'success',
  };
}

// Export standard hooks for mutations and actions
export { useAction, useMutation };
